// components/MapComponent.tsx
import React, { useEffect, useRef, useState } from "react";

interface MapComponentProps {
  center: {
    lat: number;
    lng: number;
  };
  onLocationSelect?: (lat: number, lng: number) => void;
  height?: string;
  markers?: Array<{
    lat: number;
    lng: number;
    title?: string;
  }>;
}

declare global {
  interface Window {
    kakao: any;
  }
}

const MapComponent: React.FC<MapComponentProps> = ({
  center,
  onLocationSelect,
  height = "400px",
  markers = [],
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const markerInstance = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapKey, setMapKey] = useState(0); // 지도 재생성을 위한 key

  // 카카오맵 SDK 로드 확인
  useEffect(() => {
    const checkKakaoMap = () => {
      if (window.kakao && window.kakao.maps) {
        // 이미 로드된 경우
        window.kakao.maps.load(() => {
          setIsLoaded(true);
        });
      } else {
        // 아직 로드되지 않은 경우 대기
        setTimeout(checkKakaoMap, 100);
      }
    };

    checkKakaoMap();
  }, []);

  // 지도 초기화
  useEffect(() => {
    if (!isLoaded || !mapContainer.current) return;

    console.log("지도 초기화 - center:", center); // 디버깅

    try {
      const options = {
        center: new window.kakao.maps.LatLng(center.lat, center.lng),
        level: 3,
      };

      const map = new window.kakao.maps.Map(mapContainer.current, options);
      mapInstance.current = map;

      // 지도 컨트롤 추가
      const mapTypeControl = new window.kakao.maps.MapTypeControl();
      const zoomControl = new window.kakao.maps.ZoomControl();

      map.addControl(mapTypeControl, window.kakao.maps.ControlPosition.TOPRIGHT);
      map.addControl(zoomControl, window.kakao.maps.ControlPosition.RIGHT);

      // 중심 마커 추가
      const markerPosition = new window.kakao.maps.LatLng(center.lat, center.lng);
      const marker = new window.kakao.maps.Marker({
        position: markerPosition,
        map: map,
      });
      markerInstance.current = marker;

      // 클릭 이벤트 등록
      if (onLocationSelect) {
        window.kakao.maps.event.addListener(map, "click", function (mouseEvent: any) {
          const latlng = mouseEvent.latLng;

          // 마커 위치 업데이트
          marker.setPosition(latlng);

          // 콜백 실행
          onLocationSelect(latlng.getLat(), latlng.getLng());
        });
      }

      // 추가 마커들 표시
      markers.forEach((markerData) => {
        const position = new window.kakao.maps.LatLng(markerData.lat, markerData.lng);
        const additionalMarker = new window.kakao.maps.Marker({
          position: position,
          map: map,
        });

        // 인포윈도우 추가
        if (markerData.title) {
          const infowindow = new window.kakao.maps.InfoWindow({
            content: `<div style="padding:5px;font-size:12px;">${markerData.title}</div>`,
          });

          window.kakao.maps.event.addListener(additionalMarker, "mouseover", function () {
            infowindow.open(map, additionalMarker);
          });

          window.kakao.maps.event.addListener(additionalMarker, "mouseout", function () {
            infowindow.close();
          });
        }
      });

      // 지도가 로드된 후 중심점 재설정 (타이밍 문제 해결)
      setTimeout(() => {
        if (mapInstance.current) {
          const currentCenter = new window.kakao.maps.LatLng(center.lat, center.lng);
          mapInstance.current.setCenter(currentCenter);
          mapInstance.current.relayout();
        }
      }, 100);
    } catch (err) {
      setError("지도를 초기화하는데 실패했습니다.");
      console.error("Map initialization error:", err);
    }
  }, [isLoaded, mapKey]); // mapKey 추가

  // center 변경 시 지도 중심 이동
  useEffect(() => {
    if (mapInstance.current && markerInstance.current && isLoaded) {
      console.log("Center 변경 감지:", center); // 디버깅

      const newCenter = new window.kakao.maps.LatLng(center.lat, center.lng);

      // 여러 방법 시도
      try {
        // 1. relayout 먼저 호출
        mapInstance.current.relayout();

        // 2. setCenter 시도
        mapInstance.current.setCenter(newCenter);

        // 3. 마커 위치 업데이트
        markerInstance.current.setPosition(newCenter);

        // 4. 약간의 지연 후 다시 시도
        setTimeout(() => {
          mapInstance.current.panTo(newCenter);
          // 지도 영역 재계산
          mapInstance.current.relayout();
        }, 300);
      } catch (e) {
        console.error("지도 중심 이동 실패:", e);
      }
    }
  }, [center.lat, center.lng, isLoaded]);

  // 강제 업데이트 함수
  const forceUpdateMap = () => {
    setMapKey((prev) => prev + 1);
  };

  // 로딩 중 표시
  if (!isLoaded) {
    return (
      <div
        style={{
          width: "100%",
          height: height,
          borderRadius: "4px",
          backgroundColor: "#f0f0f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#666",
        }}
      >
        지도를 불러오는 중...
      </div>
    );
  }

  // 에러 표시
  if (error) {
    return (
      <div
        style={{
          width: "100%",
          height: height,
          borderRadius: "4px",
          backgroundColor: "#fff5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#e53e3e",
          padding: "20px",
          textAlign: "center",
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div key={mapKey}>
      <div
        ref={mapContainer}
        style={{
          width: "100%",
          height: height,
          borderRadius: "4px",
        }}
      />
      {/* 디버깅용 정보 표시 */}
      <div style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
        위도: {center.lat}, 경도: {center.lng}
      </div>
    </div>
  );
};

export default MapComponent;
